import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { ACCOUNT_AUDIENCES, REFRESH_ACCOUNT_AUDIENCES } from '@/graphql/account-audiences-queries'
import { ACCOUNT_AUDIENCE_TEXT, isRefreshTooSoon, mapAccountAudiencesError } from '@/utils/account-audiences'

const NO_AUDIENCES = []

/**
 * The audiences of the customer's ad account as stored by the server. They are read from Meta only once, when
 * nothing is stored yet, and afterwards when the user asks for it. Nothing is read while `enabled` is false.
 */
export const useAccountAudiences = ({ publisherId, campaignId, enabled = true }) => {
    const active = enabled && !!publisherId && !!campaignId
    const variables = { publisherId, campaignId }
    const { data, loading, error, refetch } = useQuery(ACCOUNT_AUDIENCES, {
        variables,
        fetchPolicy: 'network-only',
        skip: !active,
    })
    const [refreshMutation, { loading: refreshing }] = useMutation(REFRESH_ACCOUNT_AUDIENCES)
    const [refreshed, setRefreshed] = useState(null)
    const [refreshError, setRefreshError] = useState(null)
    const [notice, setNotice] = useState(null)
    const autoRefreshed = useRef(false)
    const inFlight = useRef(false)

    const result = refreshed || data?.accountAudiences || null

    const refresh = useCallback(async () => {
        if (!active || inFlight.current) return
        inFlight.current = true
        setRefreshError(null)
        setNotice(null)
        try {
            const response = await refreshMutation({ variables })
            const next = response?.data?.refreshAccountAudiences
            if (next) setRefreshed(next)
        } catch (caught) {
            if (isRefreshTooSoon(caught)) {
                // The stored list is current; read it again without raising an alarm
                setNotice(ACCOUNT_AUDIENCE_TEXT.upToDate)
                try {
                    const response = await refetch()
                    if (response?.data?.accountAudiences) setRefreshed(response.data.accountAudiences)
                } catch {
                    // The list already shown stays as it is
                }
            } else {
                setRefreshError(mapAccountAudiencesError(caught))
            }
        } finally {
            inFlight.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, publisherId, campaignId, refreshMutation, refetch])

    useEffect(() => {
        if (!active) setRefreshed(null)
    }, [active])

    const neverFetched = !!data?.accountAudiences && !data.accountAudiences.fetched_at
    useEffect(() => {
        if (!neverFetched || autoRefreshed.current) return
        autoRefreshed.current = true
        refresh()
    }, [neverFetched, refresh])

    return {
        audiences: result?.audiences || NO_AUDIENCES,
        stages: result?.stages || NO_AUDIENCES,
        fetchedAt: result?.fetched_at || null,
        complete: result ? result.complete !== false : true,
        count: result?.count ?? 0,
        loading: active && loading && !result,
        refreshing,
        error: refreshError || (error && !result ? mapAccountAudiencesError(error) : null),
        notice,
        refresh,
    }
}

export default useAccountAudiences
